// src/utils/listingPrep.js
// Listing Prep (spec docs/superpowers/specs/2026-09-25-listing-prep-design.md, sections 3 to 6 and 11).
// Every decision in one pure file: who a template row goes to, when a task is due, when it is
// late and how far up the ladder it is, what blocks a move to Active, and the one-time rollout
// patch. No React, no Firestore, so Jest pins all of it. functions-api/listingPrepReminders.js
// MIRRORS the date math and PREP_STAGES; change both.
// Server code must pass the Chicago day as `today` (functions-api/timelineReminders.js
// chicagoYMD); the browser default is Minnesota's day too (src/utils/chicagoTime.js, the only
// import). addDays/toYMD intentionally duplicate timeline.js's helpers.
import { chicagoTodayYMD } from "./chicagoTime";
import { isClosedStatus } from "./taskStatus";
import { isTestFile } from "./testFiles";

export const PREP_STAGES = ["Prospect", "On Hold", "CMAs", "Listing Appointment", "Pre-Market", "Coming Soon"];

// The deal's own dates a "before" or "after" rule counts from. Same fields the Timeline links to.
// group: "listing" dates are offered on prep rows, "contract" dates on contract-stage and Buyer
// template rows (Client Care checklist spec 09/27/2026, decision 5).
export const DUE_ANCHORS = [
  { field: "targetListDate", label: "Target List Date", group: "listing" },
  { field: "photographerDate", label: "Photographer Date", group: "listing" },
  { field: "stagingInstall", label: "Staging Install", group: "listing" },
  { field: "listingAppointment", label: "Listing Appointment", group: "listing" },
  { field: "closingdate", label: "Closing Date", group: "contract" },
  { field: "inspectionDeadline", label: "Inspection Deadline", group: "contract" },
  { field: "financingDeadline", label: "Financing Deadline", group: "contract" },
  { field: "rescissionDeadline", label: "Rescission Deadline", group: "contract" },
  { field: "earnestMoneyReceivedDate", label: "Earnest Money Due", group: "contract" },
  { field: "appraisal", label: "Appraisal", group: "contract" },
  { field: "finalWalkthrough", label: "Final Walkthrough", group: "contract" },
  { field: "buyerInspection", label: "Buyer Inspection", group: "contract" },
];
const ANCHOR_FIELDS = new Set(DUE_ANCHORS.map((a) => a.field));
// Just the anchor dates a form or patch carries (unset ones left out), so a new file's checklist
// can be dated from the closing, inspection and other dates typed on its turn-in.
export const anchorDatesOf = (src) =>
  Object.fromEntries(DUE_ANCHORS.filter((a) => src && src[a.field] !== undefined).map((a) => [a.field, src[a.field]]));

export const DEFAULT_DUE_RULE = Object.freeze({ kind: "after-stage", days: 3 });

const wholeDays = (n, fallback) => {
  if (n === null || n === undefined || n === "") return fallback;
  const v = Math.floor(Number(n));
  return Number.isFinite(v) ? Math.max(0, v) : fallback;
};

// { kind: "after-stage", days } | { kind: "before-date" | "after-date", days, anchor } | { kind: "none" }
export const isDateRule = (r) => !!r && (r.kind === "before-date" || r.kind === "after-date");
export const normalizeDueRule = (rule) => {
  const kind = rule && rule.kind;
  if (kind === "none") return { kind: "none" };
  if (isDateRule(rule) && ANCHOR_FIELDS.has(rule.anchor)) {
    return { kind, days: wholeDays(rule.days, 0), anchor: rule.anchor };
  }
  if (kind === "after-stage") return { kind: "after-stage", days: wholeDays(rule.days, DEFAULT_DUE_RULE.days) };
  return { ...DEFAULT_DUE_RULE };
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const dueRuleLabel = (rule) => {
  const r = normalizeDueRule(rule);
  if (r.kind === "none") return "No date";
  if (isDateRule(r)) {
    const anchor = DUE_ANCHORS.find((a) => a.field === r.anchor);
    return `${plural(r.days, "day")} ${r.kind === "after-date" ? "after" : "before"} ${anchor ? anchor.label : r.anchor}`;
  }
  return `${plural(r.days, "day")} after stage starts`;
};

// Henry 2026-09-25: LC, EA, LC/EA and no prefix all go to the Listing Coordinator. EA is
// reserved for a later per-agent field; the prefix is returned so a future router can use it.
const PREFIX_RE = /^\s*(LC\/EA|LC|EA)\s*:/i;
export const routeByPrefix = (title) => {
  const m = PREFIX_RE.exec(String(title || ""));
  return { owner: "LC", prefix: m ? m[1].toUpperCase() : "" };
};

// ---- calendar strings, never Date arithmetic (DST) ----
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;
export const toYMD = (v) => {
  const m = YMD_RE.exec(String(v || ""));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};
export const addDays = (ymd, n) => {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m) return "";
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + Number(n || 0))).toISOString().slice(0, 10);
};
const utcMs = (ymd) => {
  const m = YMD_RE.exec(String(ymd || ""));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
};
// diffDays(a, b) = a minus b in whole days; NaN when either is not a date.
export const diffDays = (a, b) => Math.round((utcMs(a) - utcMs(b)) / 86400000);
// Today in Minnesota (America/Chicago), whatever zone the device is set to.
export const todayYMD = (now = Date.now()) => chicagoTodayYMD(now);

export const computeDeadline = (rule, { stage, deal, today } = {}) => {
  const r = normalizeDueRule(rule);
  const d = deal || {};
  if (r.kind === "none") return "";
  if (isDateRule(r)) {
    const anchor = toYMD(d[r.anchor]);
    return anchor ? addDays(anchor, r.kind === "after-date" ? r.days : -r.days) : "";
  }
  const entered = toYMD(d.stageEnteredAt && stage ? d.stageEnteredAt[stage] : "") || toYMD(today) || todayYMD();
  return addDays(entered, r.days);
};

// Still owed: anything but Completed or N/A (utils/taskStatus.js; N/A added 10/06/2026).
export const isOpenTask = (t) => !!t && !isClosedStatus(t.status);

// null = no deadline or done; 0 = due today; >0 late; <0 not yet due.
export const daysLate = (task, today) => {
  if (!isOpenTask(task) || !toYMD(task.deadline)) return null;
  const t = toYMD(today) || todayYMD();
  const n = diffDays(t, toYMD(task.deadline));
  return Number.isNaN(n) ? null : n;
};
export const isLate = (task, today) => (daysLate(task, today) || 0) > 0;

// "due" on the due day, "agent" from 1 day late (agent copied), "owner" from 3 (Henry emailed).
export const ladderStep = (late) => {
  if (late === null || late === undefined || late < 0) return null;
  if (late === 0) return "due";
  return late < 3 ? "agent" : "owner";
};

export const openRequiredTasks = (tasks) => (Array.isArray(tasks) ? tasks : []).filter((t) => t && t.requiredBeforeActive === true && isOpenTask(t));

const isBuyer = (dealtype) => String(dealtype || "").trim().toLowerCase() === "buyer";

// Does moving this deal to newStage go through the Active gate at all?
export const activeGateApplies = (deal, newStage) =>
  !!deal && newStage === "Active" && deal.dealStage !== "Active" && !isBuyer(deal.dealtype);

// The open required tasks that stop this deal from moving to newStage; [] means go ahead.
export const activeGateBlocks = (deal, newStage) => {
  if (!activeGateApplies(deal, newStage)) return [];
  return openRequiredTasks(deal.tasks);
};

export const stageEntryPatch = (existing, fromStage, toStage, today) => {
  if (!toStage || toStage === fromStage) return {};
  return { stageEnteredAt: { ...((existing && existing.stageEnteredAt) || {}), [toStage]: toYMD(today) || todayYMD() } };
};

// After one of the deal's anchor dates changes: every open before-date or after-date task that
// nobody moved by hand follows it. Moved by hand: deadlineWas or deadlineMovedAt set; those keep
// the person's date.
export const recomputeAnchoredDeadlines = (tasks, deal, today) =>
  (Array.isArray(tasks) ? tasks : []).map((t) => {
    const r = normalizeDueRule(t && t.dueRule);
    if (!t || !isDateRule(r) || !isOpenTask(t) || t.deadlineWas || t.deadlineMovedAt) return t;
    return { ...t, deadline: computeDeadline(r, { stage: t.stage, deal, today }) };
  });

const minimalPerson = (p) =>
  p && p.id
    ? { id: p.id, firstname: p.firstname || "", lastname: p.lastname || "", profilepicture: p.profilepicture || "", role: p.role || "" }
    : null;
const namedPerson = (p) => (p && p.id ? { id: p.id, firstname: p.firstname || "", lastname: p.lastname || "" } : null);
const iso = (v) => { const d = v ? new Date(v) : null; return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null; };

// The one-click jobs a task row can carry (`action`): the row shows that job's button.
// stagerTimelineEmail (Henry 10/06/2026): "Email Stager" on the TC's task added when the appraisal
// is in on a staged listing (utils/stagerTimelineTask.js). Anything else is dropped.
export const DEAL_TASK_ACTIONS = ["stagerTimelineEmail"];

// THE whitelist for deal.tasks. Every writer of the array goes through this so any field a
// writer needs must be added here; everything else is dropped on purpose (the old inline list
// in task.jsx wiped anything it did not name).
export const cleanDealTask = (t = {}) => ({
  id: t.id,
  title: t.title || "",
  status: t.status || "Open",
  stage: t.stage || null,
  section: t.section || null,
  coordinatorRole: !!t.coordinatorRole,
  transactionCoordinatorRole: !!t.transactionCoordinatorRole,
  deadline: t.deadline ? String(t.deadline) : "",
  attachments: Array.isArray(t.attachments) ? t.attachments : [],
  assignee: minimalPerson(t.assignee),
  createdAt: iso(t.createdAt),
  dueRule: normalizeDueRule(t.dueRule),
  requiredBeforeActive: t.requiredBeforeActive === true,
  routedToLC: t.routedToLC === true,
  deadlineWas: t.deadlineWas ? String(t.deadlineWas) : "",
  deadlineMovedBy: t.deadlineMovedBy ? String(t.deadlineMovedBy) : "",
  deadlineMovedAt: t.deadlineMovedAt ? String(t.deadlineMovedAt) : "",
  handedOffFrom: namedPerson(t.handedOffFrom),
  handedOffBy: t.handedOffBy ? String(t.handedOffBy) : "",
  handedOffAt: t.handedOffAt ? String(t.handedOffAt) : "",
  reminded: t.reminded && typeof t.reminded === "object" ? { ...t.reminded } : {},
  escalatedToAgentAt: t.escalatedToAgentAt ? String(t.escalatedToAgentAt) : "",
  escalatedToOwnerAt: t.escalatedToOwnerAt ? String(t.escalatedToOwnerAt) : "",
  // Work tab (Plan B, spec section 7): the template row's job kind and, once the Work tab has
  // made the job, the id of that job at deals/{dealId}/work/{jobId}. Ticking the task checks it.
  workKind: t.workKind ? String(t.workKind) : "",
  workJobId: t.workJobId ? String(t.workJobId) : "",
  // My Checklist (09/27/2026): who ticked it and when (ISO), so Undo can prove the tick is still
  // its own and the Team Checklist can say who finished what. Cleared when the task reopens.
  completedAt: t.completedAt ? String(t.completedAt) : "",
  completedBy: namedPerson(t.completedBy),
  action: DEAL_TASK_ACTIONS.includes(t.action) ? t.action : "",
  // Task Cleanup (Henry 10/06/2026, utils/taskCleanup.js): who bulk-cleaned this item up and when
  // (ISO). Only on items a cleanup touched, so every other task keeps exactly its old shape.
  ...(t.cleanedUpAt ? { cleanedUpAt: String(t.cleanedUpAt), cleanedUpBy: namedPerson(t.cleanedUpBy) } : {}),
  // Settings > Coordinators "Fix Task Owners" (Henry 10/08/2026, utils/taskOwners.js): the person the
  // rule pointed this item at, so a later run can tell the rule's choice from a hand-off.
  ...(t.ownerFixedTo ? { ownerFixedTo: String(t.ownerFixedTo) } : {}),
});

// The agent's coordinators as the minimal snapshots tasks store (users.listingCoordinatorId,
// users.transactionCoordinatorId). Only ACTIVE people count (same test as roles.js isActiveUser,
// inlined so this module stays import-free); a switched-off coordinator is treated as none.
// clientCare (My Checklist spec 09/27/2026, decision 4) is who owns buyer and contract-stage items:
// the agent's Transaction person, else the team's Backup Client Care Person
// (settings/teamDefaults.clientCareOwnerId, passed as backupId), else null (the red box).
const isActivePerson = (u) => !!u && Number(u.type) !== 1 && Number(u.type) !== 2;
export const coordinatorsFor = (agentId, users, backupId = null) => {
  const list = Array.isArray(users) ? users : [];
  const agent = list.find((u) => u && u.id === agentId);
  const pick = (id) => {
    if (!id) return null;
    const u = list.find((x) => x && String(x.id) === String(id));
    return isActivePerson(u) ? minimalPerson(u) : null;
  };
  const transactionCoordinator = pick(agent && agent.transactionCoordinatorId);
  return {
    coordinator: pick(agent && agent.listingCoordinatorId),
    transactionCoordinator,
    clientCare: transactionCoordinator || pick(backupId),
  };
};

const templateItemById = (template) => {
  const map = new Map();
  Object.values(template || {}).forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((it) => {
      if (it && it.type !== "section" && it.id !== undefined) map.set(String(it.id), it);
    });
  });
  return map;
};

// Rollout (spec section 11): stamp the current stage, assign every open task, give it the
// template's due rule and mark the required ones. Only a prep-stage listing (PREP_STAGES); an
// Active, Under Contract or Pending file is left alone. Henry 09/25/2026: existing tasks keep NO
// due date by default, so nobody is chased for hundreds of old rows that would all land on the
// same day; new work gets its date from the rule as it is created. Pass { dateExisting: true } to
// date the existing rows too. Returns the Firestore patch or null when this deal needs nothing.
// Idempotent.
export const startListingPrepPatch = (deal, template, users, today, { dateExisting = false } = {}) => {
  if (!deal || isBuyer(deal.dealtype)) return null;
  if (typeof deal.deletedAt === "number" && deal.deletedAt > 0) return null;
  if (deal.archived) return null;
  if (isTestFile(deal)) return null; // a test file is nobody's work (utils/testFiles.js)
  if (!PREP_STAGES.includes(deal.dealStage)) return null;
  const patch = {};
  const entered = { ...(deal.stageEnteredAt || {}) };
  if (deal.dealStage && !entered[deal.dealStage]) {
    entered[deal.dealStage] = toYMD(today) || todayYMD();
    patch.stageEnteredAt = entered;
  }
  const dealForDates = { ...deal, stageEnteredAt: entered };
  const { coordinator, transactionCoordinator } = coordinatorsFor(deal.agentData, users);
  const items = templateItemById(template);
  let changed = false;
  const tasks = (Array.isArray(deal.tasks) ? deal.tasks : []).map((t) => {
    if (!isOpenTask(t)) return t;
    const item = items.get(String(t.id));
    // Forward-only launch (Henry 2026-09-25): an existing undated task gets rule none so no later
    // recompute can date it; hand-dated tasks and dateExisting runs take the template rule.
    const rule = dateExisting || toYMD(t.deadline) ? normalizeDueRule(item ? item.dueRule : t.dueRule) : { kind: "none" };
    const next = { ...t };
    const ruleChanged = !t.dueRule || JSON.stringify(normalizeDueRule(t.dueRule)) !== JSON.stringify(rule);
    if (ruleChanged) { next.dueRule = rule; changed = true; }
    if (dateExisting && !toYMD(t.deadline)) {
      const d = computeDeadline(rule, { stage: t.stage, deal: dealForDates, today });
      if (d) { next.deadline = d; changed = true; }
    }
    if (item && item.requiredBeforeActive === true && t.requiredBeforeActive !== true) { next.requiredBeforeActive = true; changed = true; }
    const who = t.transactionCoordinatorRole ? transactionCoordinator : coordinator;
    if (!(t.assignee && t.assignee.id) && who) { next.assignee = who; next.routedToLC = !t.transactionCoordinatorRole; changed = true; }
    return next;
  });
  if (changed) patch.tasks = tasks;
  return Object.keys(patch).length ? patch : null;
};
